'use client'

import type { FC } from 'react'
import { useRef } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Navigation, Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IconCircle } from '../atoms/IconCircle'
import type { TimelinePhaseType } from '../atoms/TimelinePhase'
import { TimelinePhase } from '../atoms/TimelinePhase'
import { ContentBlockWrapper } from '../molecules/ContentBlockWrapper'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'

export type TimelineBlockProps = {
	timeline: TimelinePhaseType[]
}

export const TimelineBlock: FC<TimelineBlockProps> = ({ timeline }) => {
	const swiperRef = useRef<SwiperType | null>(null)

	const handlePrev = () => {
		if (swiperRef.current) {
			swiperRef.current.slidePrev()
		}
	}

	const handleNext = () => {
		if (swiperRef.current) {
			swiperRef.current.slideNext()
		}
	}

	return (
		<ContentBlockWrapper blockTypes={['component']} layout="nested" className="overflow-hidden">
			<div className="col-span-full">
				<div className="w-full">
					{/* Desktop and Tablet Swiper */}
					<div className="hidden tablet-l:block">
						<Swiper
							onSwiper={(swiper) => {
								swiperRef.current = swiper
							}}
							modules={[Navigation, Scrollbar]}
							slidesPerView={3}
							scrollbar={{
								hide: false,
								draggable: true,
								el: '.custom-scrollbar',
							}}
							breakpoints={{
								1024: {
									slidesPerView: 3.1,
								},
								1280: {
									slidesPerView: 4,
								},
							}}
							className="!overflow-visible"
						>
							{timeline.map((phase, index) => (
								<SwiperSlide key={phase.id} className="!opacity-100">
									<TimelinePhase timelinePhase={phase} isLast={index === timeline.length - 1} />
								</SwiperSlide>
							))}
							{timeline.length > 3 && (
								<div className="flex items-center gap-x-xl pt-5xl">
									<div className="!relative flex flex-1 items-center">
										<div className="custom-scrollbar swiper-scrollbar !w-full" />
									</div>
									{/*Controls */}
									<div className="flex items-center justify-center gap-x-lg">
										<button
											onClick={handlePrev}
											className="group cursor-pointer"
											aria-label="Previous"
										>
											<IconCircle icon={['fas', 'chevron-left']} size="sm" />
										</button>

										<button onClick={handleNext} className="group cursor-pointer" aria-label="Next">
											<IconCircle icon={['fas', 'chevron-right']} size="sm" />
										</button>
									</div>
								</div>
							)}
						</Swiper>
					</div>

					{/* Mobile Stacked Layout */}
					<div className="block tablet-l:hidden">
						<div className="flex flex-col">
							{timeline.map((phase, index) => (
								<TimelinePhase
									key={index}
									timelinePhase={phase}
									isLast={index === timeline.length - 1}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</ContentBlockWrapper>
	)
}
